import React, { useRef, useState, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import Vimeo from '@vimeo/player';
import YouTube from 'react-youtube';
import Dailymotion from 'react-dailymotion';
import { basedAxios } from '../../store/Store';
import VideoThumbnail from 'react-video-thumbnail';
import { Player, ControlBar } from 'video-react';
import 'video-react/dist/video-react.css';
import EventLabel from '../../components/EventLabel/EventLabel';

export const VideoModule = observer(({ settings, setAspectRatio, ...rest }) => {
  const [forceThumbnail, setForceThumbnail] = useState();
  const { style = {}, hidden } = settings;

  useEffect(() => {
    setAspectRatio('16 / 9');
  }, []);

  const shareVideo = async () => {
    try {
      const order = rest?.orders[0];
      const eventId = order?.eventId;
      const badgeNo = order?.badgeNo;
      const videoUrl = `https://jirangopub.s3.eu-north-1.amazonaws.com/Registration/${eventId}/axe_track_${badgeNo}.mp4`;

      // Fetch the image or video
      const response = await fetch(videoUrl);

      // Determine the file type based on the URL
      const contentType = response.headers.get('content-type');
      const extension = contentType.startsWith('image') ? 'png' : 'mp4';

      // Read the response as blob
      const blob = await response.blob();

      // Create a File object
      const file = new File([blob], `axe_music.${extension}`, { type: contentType });

      // Share the file if sharing is supported
      const title = 'title';

      try {
        await basedAxios.post('./utility/clicktrack', null, {
          params: {
            eventId: eventId,
            badgeNo: badgeNo,
            area: 'axe',
            value: videoUrl,
            name: 'video-shared',
          },
        });
      } catch (error) {}

      if (
        navigator.canShare &&
        navigator.canShare({
          title: `${title}`,
          files: [file],
        })
      ) {
        await navigator.share({
          title: `${title}`,
          files: [file],
        });
      }
    } catch (error) {
      console.error('Error sharing: ' + error);
    }
  };

  const source = (() => {
    if (settings.source && settings.source?.toLowerCase().includes('[axe_track]')) {
      const order = rest.orders[rest.activePersonNo];
      const eventId = order?.eventId;
      const badgeNo = order?.badgeNo;

      return `https://jirangopub.s3.eu-north-1.amazonaws.com/Registration/${eventId}/axe_track_${badgeNo}.mp4`;
    }
    return settings.source;
  })();

  if (hidden) return null;

  return (
    <div style={{ ...style, position: 'relative', width: '100%', height: '100%' }}>
      <GenericPlayer
        {...{
          ...settings,
          source,
          thumbnail: forceThumbnail || settings.thumbnail,
        }}
      />
      {settings.source === '[axe_track]' && rest?.ordersLoaded && (
        <div id="video-share-button">
          <button className="my-4" onClick={shareVideo}>
            <i className="ri-share-fill"></i>
            <EventLabel name="ShareVideo" groupName="Cms.Video" />
          </button>
        </div>
      )}
    </div>
  );
});

export const GenericPlayer = observer(settings => {
  const url = settings.source;
  const thumbnail = settings.thumbnail;

  if (!url) return <VideoPlayer url={''} thumbnail={thumbnail} />;
  else if (url.includes('vimeo')) return <VimeoPlayer url={url} />;
  else if (url.includes('youtube') || url.includes('youtu.be'))
    return (
      <div className="embeded-frame">
        <YouTubePlayer url={url} />
      </div>
    );
  else if (url.includes('dailymotion') || url.includes('https://dai.ly/'))
    return (
      <div className="embeded-frame">
        <DailyMotionPlayer url={url} />
      </div>
    );
  else if (url.includes('jirango')) {
    return <VideoPlayer url={url} />;
  } else {
    if (thumbnail.includes('http')) {
      return <VideoPlayer url={url} thumbnail={thumbnail} />;
    }
    return (
      <iframe
        title="Player"
        frameBorder="0"
        scrolling="0"
        allowFullScreen
        allow="autoplay; fullscreen"
        style={{ width: '100%', height: '100%', border: '0px' }}
        src={url}
      />
    );
  }
});

const VimeoPlayer = ({ url, thumbnail }) => {
  const [played, setPlayed] = useState(false);
  const [videoId, setVideoId] = useState(null);
  const iframeRef = useRef(null);
  const playedRef = useRef();
  const playerUrl = `https://player.vimeo.com/video/${videoId}`;
  const extractIdFromUrl = () => {
    if (/vimeo.com\/(\d+)/i.test(url)) {
      setVideoId(/vimeo.com\/(\d+)/i.exec(url)[1]);
    } else if (/player.vimeo.com\/video\/(\d+)/i.test(url)) {
      setVideoId(/player.vimeo.com\/video\/(\d+)/i.exec(url)[1]);
    }
  };

  useEffect(() => {
    extractIdFromUrl();
  }, [url]);

  useEffect(() => {
    playedRef.current = played;
  }, [played]);

  useEffect(() => {
    const player = new Vimeo(iframeRef.current);
    player.on('play', function () {
      if (playedRef.current) return;
      setPlayed(true);
    });
    return () => {
      iframeRef.current = null;
    };
  }, []);

  return (
    <iframe
      allowFullScreen={true}
      style={{ width: '100%', height: '100%', border: '0px' }}
      allow="autoplay; fullscreen; picture-in-picture"
      ref={iframeRef}
      src={playerUrl}
    />
  );
};

const YouTubePlayer = ({ url }) => {
  const [played, setPlayed] = useState(false);
  const onPlay = () => {
    if (played) return;
    setPlayed(true);
  };
  const extractIdFromUrl = () => {
    let startString = '';
    if (url.includes('https://www.youtube.com/watch?v=')) {
      startString = 'https://www.youtube.com/watch?v=';
      var tempString = url.replace(startString, '');

      return tempString.substring(0, tempString.includes('?') ? tempString.indexOf('?') : tempString.length);
    } else if (url.includes('https://youtu.be/')) {
      startString = 'https://youtu.be/';
      var tempString = url.replace(startString, '');
      return tempString.substring(0, tempString.includes('?') ? tempString.indexOf('?') : tempString.length);
    } else if (url.includes('https://www.youtube.com/embed/')) {
      startString = 'https://www.youtube.com/embed/';
      var tempString = url.replace(startString, '');

      return tempString.substring(0, tempString.includes('?') ? tempString.indexOf('?') : tempString.length);
    }
  };

  const id = useMemo(() => {
    return extractIdFromUrl();
  }, [url]);

  const opts = {
    height: '100%',
    width: '100%',
    playerVars: {
      autoplay: url.toString().includes('autoplay=1') ? 1 : 0,
    },
  };

  return <YouTube videoId={id} className="yt-video" opts={opts} onPlay={onPlay} />;
};

const DailyMotionPlayer = ({ url, thumbnail }) => {
  const [played, setPlayed] = useState(false);
  const extractIdFromUrl = () => {
    let startString = '';

    try {
      if (url.includes('https://dai.ly/')) {
        startString = 'https://dai.ly/';
        var tempString = url.replace(startString, '');

        return tempString.substring(0, tempString.includes('?') ? tempString.indexOf('?') : tempString.length);
      } else if (url.includes('https://www.dailymotion.com/video/')) {
        startString = 'https://www.dailymotion.com/video/';
        var tempString = url.replace(startString, '');

        return tempString.substring(0, tempString.includes('?') ? tempString.indexOf('?') : tempString.length);
      }
    } catch (error) {
      if (!url) return '';
    }
  };

  const id = useMemo(() => {
    return extractIdFromUrl();
  }, [url]);
  return (
    <iframe src={`https://www.dailymotion.com/embed/video/${id}?autoplay=0`} style={{ width: '100%', height: '100%', border: '0px' }} allowfullscreen></iframe>
  );
};

const VideoPlayer = ({ url, thumbnail, downloadable }) => {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        backgroundColor: '#000',
      }}
    >
      <Player src={url} className="video-react-big-play-button-center" preload="auto" style={{ width: '100%', height: '100%' }} poster={thumbnail} controlsList="nodownload">
        <ControlBar autoHideTime />
      </Player>
    </div>
  );
};
